import { NgClass, UpperCasePipe } from '@angular/common';
import { Component, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { openContactSupportDrawer } from '@capital-access/common/actions';
import { CommonLocalizationModule } from '@capital-access/common/localization';
import { FireflyFormattingModule, FireflySuggesterModule } from '@capital-access/firefly/components';
import { COMMON_COMPONENTS_LOCALIZATION_SCOPE } from '../../common-components.const';
import { SaveFilterCriteria } from './models/save-filter-criteria.model';
import { SavedFilterType } from './models/saved-filter-type.model';
import { CriteriaNamesItem } from './models/saved-filters.model';
import { SavedFiltersSingleSuggesterStore } from './saved-filters-single-suggester.store';

@Component({
  selector: 'ca-saved-filters',
  standalone: true,
  imports: [CommonLocalizationModule, FireflySuggesterModule, UpperCasePipe, FireflyFormattingModule],
  templateUrl: './saved-filters.component.html',
  providers: [SavedFiltersSingleSuggesterStore]
})
export class SavedFiltersComponent implements OnInit {
  localizationScope = COMMON_COMPONENTS_LOCALIZATION_SCOPE;
  readonly suggesterStore = inject(SavedFiltersSingleSuggesterStore);
  private readonly destroyRef = inject(DestroyRef);
  readonly idPath = 'id';
  readonly valuePath = 'name';
  readonly isSavedFiltersLimitReached = this.suggesterStore.isSavedFiltersLimitReached;
  private store = inject(Store);
  filterType = input.required<SavedFilterType>();
  onSelectedSavedFilterCriteria = output<SaveFilterCriteria | null>();
  isLoadingSelectedSavedFilter = output<boolean>();

  ngOnInit(): void {
    this.suggesterStore.setInitialData(this.filterType());
  }

  onSelect(item: unknown) {
    const selectedFilter = item as CriteriaNamesItem;
    this.onValueChange(selectedFilter);
  }

  onReset() {
    this.onValueChange(null);
  }

  onContactSupport() {
    this.store.dispatch(openContactSupportDrawer());
  }

  private onValueChange(selectedFilter: CriteriaNamesItem | null): void {
    this.suggesterStore.setSelectedFilter(selectedFilter);
    if (!selectedFilter?.id) {
      this.isLoadingSelectedSavedFilter.emit(false);
      this.onSelectedSavedFilterCriteria.emit(null);
      return;
    }
    this.isLoadingSelectedSavedFilter.emit(true);
    this.suggesterStore
      .setSelectedSavedFilter(selectedFilter.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: criteria => {
          this.isLoadingSelectedSavedFilter.emit(false);
          this.onSelectedSavedFilterCriteria.emit(criteria);
        },
        error: () => {
          this.isLoadingSelectedSavedFilter.emit(false);
        }
      });
  }

  onDeleteFilter(item: unknown) {
    const selectedFilter = item as CriteriaNamesItem;
    this.suggesterStore.openDeleteFilterModal(selectedFilter);
  }
}
